/**
 * Filesystem-safe name building for anything we hand to the user as a file:
 * zip entry paths (`workers/zipExportWorker.js`) and the `download` attribute
 * on single-asset downloads (`hooks/useFileDownload.js`).
 *
 * Asset, folder and character names are free text, so they routinely contain
 * characters that are illegal, invisible, or actively misleading once they land
 * on a real filesystem. Everything here is pure and dependency-free so the
 * module Web Worker can import it too.
 */

// Per-segment byte budget. Filesystems cap a path component at 255 bytes
// (ext4/APFS/NTFS), and we leave generous headroom for an extension plus the
// " (2)" collision suffix the zip worker appends — while keeping nested
// `root/folder/file` paths short enough to stay clear of Windows' MAX_PATH.
export const MAX_SEGMENT_BYTES = 120;

// Legal as a filename on POSIX, reserved on Windows — and `/` and `\` would
// silently turn a name into a nested directory inside the archive.
const ILLEGAL_CHARS = /[<>:"/\\|?*]/g;

// C0 and C1 control ranges. Newlines and NUL in particular break archive
// tooling and shell round-trips.
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;

// Bidi overrides and zero-width characters. These are the classic extension
// spoof: "invoice\u202Egnp.exe" renders to a human as "invoicexe.png" while
// the filesystem sees an .exe. Also strips BOM and zero-width joiners, which
// are invisible but still count toward the name.
const DECEPTIVE_CHARS =
  /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;

// Windows device names are reserved with or without an extension, so `NUL.png`
// is just as unusable as `NUL`.
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

// What actually counts as an extension: a short, unspaced alphanumeric tail.
// Asset names routinely contain dots that are not extensions ("seedance 2.0 -
// bears"), so a naive lastIndexOf('.') both mangles the name and suppresses the
// real extension we'd otherwise append from the url.
const EXTENSION_PATTERN = /\.[A-Za-z0-9]{1,10}$/;

const encoder = new TextEncoder();

const byteLength = (value: string): number => encoder.encode(value).length;

/**
 * Split a name into `{ base, extension }`, where `extension` includes the dot
 * and is `''` when the name has no extension-looking tail.
 */
export function splitExtension(value: unknown): {
  base: string;
  extension: string;
} {
  const name = String(value ?? "");
  const match = EXTENSION_PATTERN.exec(name);
  // `index === 0` means the whole name is the "extension" (".png") — treat it
  // as a basename instead of a file with no name.
  if (!match || match.index === 0) return { base: name, extension: "" };
  return { base: name.slice(0, match.index), extension: match[0] };
}

// Truncate on a code-point boundary so multi-byte characters (emoji, CJK) are
// never cut in half into a lone surrogate.
function truncateToBytes(value: string, maxBytes: number): string {
  if (byteLength(value) <= maxBytes) return value;
  let out = "";
  let bytes = 0;
  for (const char of value) {
    const size = byteLength(char);
    if (bytes + size > maxBytes) break;
    out += char;
    bytes += size;
  }
  return out;
}

/**
 * Strips everything that would make a path component illegal, invisible, or
 * deceptive. Shared by the file and folder variants below.
 */
function scrub(value: unknown): string {
  return (
    String(value ?? "")
      .replace(ILLEGAL_CHARS, "_")
      .replace(CONTROL_CHARS, "")
      .replace(DECEPTIVE_CHARS, "")
      // Collapse the remaining whitespace (tabs already gone as control chars)
      .replace(/\s+/g, " ")
      // Leading dots would hide the file on POSIX, and a name of only dots
      // ("." / "..") would resolve to a directory rather than an entry.
      // Trailing dots and spaces are silently dropped by Windows, so a name
      // that relies on them wouldn't survive extraction anyway.
      .replace(/^[.\s]+/, "")
      .replace(/[.\s]+$/, "")
      .trim()
  );
}

function deReserve(value: string): string {
  // Test the part before the first dot: `CON`, `CON.txt` and `con.png` are
  // all reserved.
  const base = value.split(".")[0];
  return WINDOWS_RESERVED.test(base) ? `_${value}` : value;
}

/**
 * Sanitize one path component — a folder name inside a zip, typically.
 */
export function sanitizePathSegment(
  value: unknown,
  fallback = "Untitled",
): string {
  const scrubbed = deReserve(scrub(value));
  if (!scrubbed) return fallback;
  // Re-scrub the tail: truncation can re-expose a trailing dot or space.
  const truncated = truncateToBytes(scrubbed, MAX_SEGMENT_BYTES).replace(
    /[.\s]+$/,
    "",
  );
  return truncated || fallback;
}

/**
 * Sanitize a file name, keeping the extension intact when the name has to be
 * shortened — a truncated basename is still openable, a truncated extension is
 * not. `.tar.gz`-style double extensions keep only the last part, which is what
 * the OS looks at anyway.
 */
export function sanitizeFileName(value: unknown, fallback = "file"): string {
  const scrubbed = deReserve(scrub(value));
  if (!scrubbed) return fallback;

  const { base, extension } = splitExtension(scrubbed);

  const truncatedBase = truncateToBytes(
    base,
    MAX_SEGMENT_BYTES - byteLength(extension),
  )
    // Trailing dot/space would collide with the extension separator.
    .replace(/[.\s]+$/, "");

  return `${truncatedBase || fallback}${extension}`;
}

/**
 * The extension the archive would append, read off the stored url. Assets carry
 * a display name rather than a filename, so the extension almost never comes
 * from the name itself.
 *
 * @input 'https://cdn.example.com/a/b/clip.mp4?sv=2024'
 * @output '.mp4'
 */
export function getExtensionFromUrl(url: unknown): string {
  try {
    const pathname = new URL(String(url)).pathname;
    const lastSegment = pathname.split("/").pop() || "";
    return splitExtension(lastSegment).extension;
  } catch {
    return "";
  }
}

/**
 * The name an asset is written under inside a download archive: its display
 * name made filesystem-safe, with the url's extension restored when the name
 * carries none.
 *
 * This is the single definition of that name. The zip worker writes files by
 * it and the edit decision list exporter points `<pathurl>` at it, and a clip
 * goes offline in Premiere the moment those two disagree.
 *
 * @input {name: 'Seedance 2.0 (2026-09-09 16:29)', url: 'https://cdn.example.com/x.mp4'}
 * @output 'Seedance 2.0 (2026-09-09 16_29).mp4'
 */
export function toArchiveFileName({
  name,
  url,
}: {
  name: unknown;
  url: unknown;
}): string {
  const sanitized = sanitizeFileName(name);

  if (splitExtension(sanitized).extension) return sanitized;

  return `${sanitized}${getExtensionFromUrl(url)}`;
}

/**
 * Appends the archive's collision suffix. `usedNames` is carried across a whole
 * group of files and mutated as each name is claimed, exactly as the zip worker
 * does it.
 *
 * @input {usedNames: Map{'clip.mp4' => 1}, fileName: 'clip.mp4'}
 * @output 'clip (2).mp4'
 */
export function claimUniqueArchiveFileName({
  usedNames,
  fileName,
}: {
  usedNames: Map<string, number>;
  fileName: string;
}): string {
  const timesUsed = usedNames.get(fileName) || 0;
  usedNames.set(fileName, timesUsed + 1);

  if (timesUsed === 0) return fileName;

  const { base, extension } = splitExtension(fileName);
  return `${base} (${timesUsed + 1})${extension}`;
}
